import {
  isLoaded,
  question,
  UnassignedUWQuestions,
  UWQuestionsError,
  UWQuestionsModel,
} from 'ApplicationEntry/models/UWQuestionsModel';
import {Loaded} from 'Core/models/Async';
import {flow} from 'fp-ts/lib/function';
import * as I from 'immutable';
import {ToPartialMatchOptions} from 'Shared/utilities/MapDiscriminatedUnion';
import {Height, ValidHeight} from './Height';
import {safeParseInt} from 'Shared/utilities/parsers';
import * as O from 'fp-ts/lib/Option';
export class UnvalidatedUWQuestions {
  public readonly key = 'UnvalidatedUWQuestions';

  constructor(
    public readonly questionList: UWQuestionsModel,
    public readonly answers: I.List<boolean | string>,
    public readonly height?: Height,
    public readonly weight?: number | undefined | '',
    public readonly spouseHeight?: Height,
    public readonly spouseWeight?: number | undefined | '',
    public readonly childHeight?: Height,
    public readonly childWeight?: number | undefined | ''
  ) {}
}

export class InvalidUWQuestions {
  public readonly key = 'InvalidUWQuestions';

  constructor(
    public readonly questionList: UWQuestionsModel,
    public readonly answers: I.List<boolean | string>,
    public readonly height?: Height,
    public readonly weight?: number | undefined | '',
    public readonly spouseHeight?: Height,
    public readonly spouseWeight?: number | undefined | '',
    public readonly childHeight?: Height,
    public readonly childWeight?: number | undefined | ''
  ) {}
}

type AppProps = {
  questionList: UWQuestionsModel;
  answers: I.List<boolean | string>;
  height?: Height;
  weight?: number | undefined | '';
  spouseHeight?: Height;
  spouseWeight?: number | undefined | '';
  childHeight?: Height;
  childWeight?: number | undefined | '';
};

export class ValidUWQuestions {
  public static createFromApp({
    questionList,
    answers,
    height,
    weight,
    spouseHeight,
    spouseWeight,
    childHeight,
    childWeight,
  }: AppProps) {
    return this.create(questionList, answers, height, weight, spouseHeight, spouseWeight, childHeight, childWeight);
  }

  public static create(
    questionList: UWQuestionsModel,
    answers: I.List<boolean | string>,
    height: Height | undefined,
    weight: number | undefined | '',
    spouseHeight?: Height,
    spouseWeight?: number | undefined | '',
    childHeight?: Height,
    childWeight?: number | undefined | ''
  ) {
    return isLoaded(questionList)
      ? new ValidUWQuestions(
          questionList,
          answers,
          height,
          weight,
          spouseHeight,
          spouseWeight,
          childHeight,
          childWeight
        )
      : new InvalidUWQuestions(questionList, answers);
  }

  public readonly key = 'ValidUWQuestions';
  private constructor(
    public readonly questionList: Loaded<I.List<question>>,
    public readonly answers: I.List<boolean | string>,
    public readonly height?: Height,
    public readonly weight?: number | undefined | '',
    public readonly spouseHeight?: Height,
    public readonly spouseWeight?: number | undefined | '',
    public readonly childHeight?: Height,
    public readonly childWeight?: number | undefined | ''
  ) {}
}

export type UWQuestions = UnvalidatedUWQuestions | InvalidUWQuestions | ValidUWQuestions;

export type UWQuestionsOptions<TReturn> = ToPartialMatchOptions<UWQuestions, 'key', TReturn>;

export const matchUWQuestions = <TReturn>(options: UWQuestionsOptions<TReturn>) => (app: UWQuestions): TReturn => {
  switch (app.key) {
    case 'InvalidUWQuestions':
      return options.InvalidUWQuestions ? options.InvalidUWQuestions(app) : options.default(app);
    case 'UnvalidatedUWQuestions':
      return options.UnvalidatedUWQuestions ? options.UnvalidatedUWQuestions(app) : options.default(app);
    case 'ValidUWQuestions':
      return options.ValidUWQuestions ? options.ValidUWQuestions(app) : options.default(app);
  }
};

export const changeQuestions = (questions: UWQuestionsModel) => (app: UWQuestions) =>
  ValidUWQuestions.createFromApp({
    ...app,
    questionList: questions,
  });

export const changeAnswers = (answer: boolean, index: number) => (app: UWQuestions) => {
  const existingList: I.List<boolean | string> = app.answers;
  let newList: I.List<boolean | string> = existingList;
  let newAnswer: boolean | string = '';
  if (existingList.get(index) !== answer) {
    newAnswer = answer;
  }
  newList = existingList.splice(index, 1, newAnswer);

  return ValidUWQuestions.createFromApp({
    ...app,
    answers: newList,
  });
};

export const changeAnswersforAllQuestions = (answer: boolean, index: number) => (app: UWQuestions) => {
  const existingList: I.List<boolean | string> = app.answers;
  let newList: I.List<boolean | string> = existingList;
  let newAnswer: boolean | string = '';
  newAnswer = answer === true ? true : '';

  newList = existingList.splice(index, 1, newAnswer);
  if (newList.every(item => item === true)) {
    return ValidUWQuestions.createFromApp({
      ...app,
      answers: newList,
    });
  } else {
    return ValidUWQuestions.createFromApp({
      ...app,
      answers: newList,
    });
  }
};

export const changeAnswersforAllQuestionsNoCheckBox = (answer: boolean, index: number) => (app: UWQuestions) => {
  const existingList: I.List<boolean | string> = app.answers;
  let newList: I.List<boolean | string> = existingList;
  let newAnswer: boolean | string = '';
  newAnswer = answer === true ? false : '';

  newList = existingList.splice(index, 1, newAnswer);
  if (newList.every(item => item === true)) {
    return ValidUWQuestions.createFromApp({
      ...app,
      answers: newList,
    });
  } else {
    return ValidUWQuestions.createFromApp({
      ...app,
      answers: newList,
    });
  }
};

export const removeQuestions = () => (app: UWQuestions) => {
  return ValidUWQuestions.createFromApp({
    ...app,
    questionList: new UWQuestionsError(),
  });
};

export const UnassignQuestions = () => (app: UWQuestions) => {
  return ValidUWQuestions.createFromApp({
    ...app,
    questionList: new UnassignedUWQuestions(),
  });
};

export const changeMultAnswers = (answers: I.List<boolean | string>) => (app: UWQuestions) =>
  ValidUWQuestions.createFromApp({...app, answers});

export const EmptyUWQuestions = new UnvalidatedUWQuestions(new UnassignedUWQuestions(), I.List<boolean | string>());
export const changeHeight = (feet: number | '', inches: number | '', targetElement: string | '') => (
  app: UWQuestions
) => {
  if (targetElement === 'EmployeeHeight')
    return ValidUWQuestions.createFromApp({...app, height: ValidHeight.create(feet, inches)});
  if (targetElement === 'SpouseHeight')
    return ValidUWQuestions.createFromApp({...app, spouseHeight: ValidHeight.create(feet, inches)});
  else return ValidUWQuestions.createFromApp({...app, childHeight: ValidHeight.create(feet, inches)});
};

export const changeWeight = (weight: string, targetElement: string | '') => (app: UWQuestions) => {
  const parseWeight = flow(
    safeParseInt,
    O.getOrElse<number | ''>(() => '')
  );
  if (targetElement === 'EmployeeWeight') return ValidUWQuestions.createFromApp({...app, weight: parseWeight(weight)});
  else if (targetElement === 'SpouseWeight')
    return ValidUWQuestions.createFromApp({...app, spouseWeight: parseWeight(weight)});
  else return ValidUWQuestions.createFromApp({...app, childWeight: parseWeight(weight)});
};
